import React from 'react';
import { Card as MuiCard, CardContent as MuiCardContent, CardHeader as MuiCardHeader, CardActions as MuiCardActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import spacing from '../spacing';
import shadows from '../shadows';

/**
 * Card Component
 * @component
 * @description Modern card with rounded borders, soft shadows, and multiple variants
 * 
 * @example
 * // Simple card
 * <Card>
 *   <CardHeader title="Título" subheader="Subtítulo" />
 *   <CardContent>Contenido</CardContent>
 *   <CardActions>
 *     <Button>Acción</Button>
 *   </CardActions>
 * </Card>
 * 
 * @example
 * // Card with green header
 * <Card greenHeader>
 *   <CardHeader title="Destacado" />
 *   <CardContent>Contenido importante</CardContent>
 * </Card>
 * 
 * @param {string} variant - 'elevated' | 'outlined' | 'flat'
 * @param {number} elevation - Custom elevation (0-24)
 * @param {boolean} greenHeader - Use green header style
 */

const StyledCard = styled(MuiCard)(({ variant, elevation, theme }) => ({
  borderRadius: spacing.borderRadius.lg,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  backgroundImage: 'none',
  
  // Variant: elevated (default)
  ...(variant === 'elevated' && {
    boxShadow: shadows.card,
    '&:hover': {
      boxShadow: shadows.cardHover,
      transform: 'translateY(-2px)',
    },
  }),

  // Variant: outlined
  ...(variant === 'outlined' && {
    border: `${spacing.borderWidth[1]}px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    '&:hover': {
      borderColor: theme.palette.primary.light,
      boxShadow: shadows.sm,
    },
  }),

  // Variant: flat
  ...(variant === 'flat' && {
    boxShadow: 'none',
    backgroundColor: theme.palette.action.hover,
  }),

  // Custom elevation
  ...(elevation && {
    boxShadow: shadows.elevation[elevation],
  }),
}));

const StyledCardHeader = styled(MuiCardHeader)(({ theme }) => ({
  padding: spacing[4],
  borderBottom: `1px solid ${theme.palette.divider}`,
  
  '& .MuiCardHeader-title': {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  
  '& .MuiCardHeader-subheader': {
    color: theme.palette.text.secondary,
  },
}));

const StyledCardContent = styled(MuiCardContent)({
  padding: spacing[4],
  
  '&:last-child': {
    paddingBottom: spacing[4],
  },
});

const StyledCardActions = styled(MuiCardActions)({
  padding: spacing[4],
  paddingTop: 0,
  gap: spacing[2],
});

// Card con header verde (para destacar)
const GreenHeaderCard = styled(StyledCard)(({ theme }) => ({
  '& .MuiCardHeader-root': {
    backgroundColor: theme.palette.primary.main,
    borderBottom: 'none',

    '& .MuiCardHeader-title': {
      color: theme.palette.primary.contrastText,
    },

    '& .MuiCardHeader-subheader': {
      color: theme.palette.primary.contrastText,
      opacity: 0.9,
    },

    '& .MuiCardHeader-avatar': {
      color: theme.palette.primary.contrastText,
    },
  },
}));

const Card = ({
  children,
  variant = 'elevated',
  elevation,
  greenHeader = false,
  ...props
}) => {
  const CardComponent = greenHeader ? GreenHeaderCard : StyledCard;
  
  return (
    <CardComponent variant={variant} elevation={elevation} {...props}>
      {children}
    </CardComponent>
  );
};

const CardHeader = ({ title, subheader, avatar, action, ...props }) => {
  return (
    <StyledCardHeader
      title={title}
      subheader={subheader}
      avatar={avatar}
      action={action}
      {...props}
    />
  );
};

const CardContent = ({ children, ...props }) => {
  return (
    <StyledCardContent {...props}>
      {children}
    </StyledCardContent>
  );
};

const CardActions = ({ children, ...props }) => {
  return (
    <StyledCardActions {...props}>
      {children}
    </StyledCardActions>
  );
};

export { Card, CardHeader, CardContent, CardActions };
export default Card;
